"use client"

import { useEffect, useRef, useState } from "react"
import { MessageCircle, UserRound } from "lucide-react"

import type { Character } from "@/shared/types/character"
import { ROUTES } from "@/shared/lib/constants"
import { cn } from "@/shared/lib/utils"
import { getCharacterCreatorLabel } from "@/shared/lib/character-creator"
import { LocalizedLink } from "@/shared/components/i18n/localized-link"
import { ImageWithFallback } from "@/shared/components/image-with-fallback"
import { ImageFallback } from "@/shared/components/ui/image-fallback"
import { VideoAutoplayFallback } from "@/shared/components/video-autoplay-fallback"
import { useLocalizedCharacter } from "@/features/characters/hooks/use-localized-character"

interface CharacterCardProps {
  character: Character
  variant?: "default" | "compact"
  priority?: boolean
  sticker?: string
}

function formatConversationCount(count: number): string {
  const roundedCount = Math.max(1, Math.round(count))
  if (roundedCount >= 1000) return `${(roundedCount / 1000).toFixed(1)}M`
  return `${roundedCount}K`
}

type RegisteredVideo = {
  element: HTMLVideoElement
  setIsActive: (value: boolean) => void
}

const registeredVideos = new Map<HTMLVideoElement, RegisteredVideo>()
let hoveredVideo: HTMLVideoElement | null = null

function updateActiveVideoSlots() {
  const hoveredItem = hoveredVideo ? registeredVideos.get(hoveredVideo) : null
  const activeElements = new Set<HTMLVideoElement>()

  if (hoveredItem) {
    activeElements.add(hoveredItem.element)
  }

  registeredVideos.forEach((item) => {
    item.setIsActive(activeElements.has(item.element))
  })
}

function registerLimitedVideo(
  element: HTMLVideoElement,
  setIsActive: (value: boolean) => void
) {
  const cardElement = element.closest("div.group.relative.overflow-hidden")

  registeredVideos.set(element, {
    element,
    setIsActive,
  })

  const handlePointerEnter = () => setHoveredLimitedVideo(element)
  const handlePointerLeave = () => {
    if (hoveredVideo === element) setHoveredLimitedVideo(null)
  }

  cardElement?.addEventListener("pointerenter", handlePointerEnter)
  cardElement?.addEventListener("pointerleave", handlePointerLeave)
  cardElement?.addEventListener("focusin", handlePointerEnter)
  cardElement?.addEventListener("focusout", handlePointerLeave)
  updateActiveVideoSlots()

  return () => {
    cardElement?.removeEventListener("pointerenter", handlePointerEnter)
    cardElement?.removeEventListener("pointerleave", handlePointerLeave)
    cardElement?.removeEventListener("focusin", handlePointerEnter)
    cardElement?.removeEventListener("focusout", handlePointerLeave)
    registeredVideos.delete(element)
    setIsActive(false)
    updateActiveVideoSlots()
  }
}

function setHoveredLimitedVideo(element: HTMLVideoElement | null) {
  hoveredVideo = element
  updateActiveVideoSlots()
}

type BoomerangDirection = "forward" | "reverse"

function useBoomerangVideo(forwardSrc?: string | null, reverseSrc?: string | null) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const reverseVideoRef = useRef<HTMLVideoElement | null>(null)
  const [playbackDirection, setPlaybackDirection] = useState<BoomerangDirection>("forward")
  const [visibleDirection, setVisibleDirection] = useState<BoomerangDirection>("forward")
  const [isVideoVisible, setIsVideoVisible] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const [autoplayBlocked, setAutoplayBlocked] = useState(false)
  const [isManualPlayback, setIsManualPlayback] = useState(false)

  useEffect(() => {
    const video = videoRef.current
    if (!video || !forwardSrc) return

    return registerLimitedVideo(video, setIsActive)
  }, [forwardSrc])

  useEffect(() => {
    setPlaybackDirection("forward")
    setVisibleDirection("forward")
    setIsVideoVisible(false)
    setAutoplayBlocked(false)
    setIsManualPlayback(false)
  }, [forwardSrc, reverseSrc])

  useEffect(() => {
    const forwardVideo = videoRef.current
    const reverseVideo = reverseVideoRef.current
    const activeVideo = playbackDirection === "reverse" ? reverseVideo : forwardVideo
    const inactiveVideo = playbackDirection === "reverse" ? forwardVideo : reverseVideo
    const activeSrc = playbackDirection === "reverse" ? reverseSrc : forwardSrc

    if (!isActive) {
      ;[forwardVideo, reverseVideo].forEach((video) => {
        if (!video) return
        video.pause()
        video.currentTime = 0
      })
      setPlaybackDirection("forward")
      setVisibleDirection("forward")
      setIsVideoVisible(false)
      setAutoplayBlocked(false)
      setIsManualPlayback(false)
      return
    }

    if (!activeVideo || !activeSrc) return

    inactiveVideo?.pause()
    activeVideo.currentTime = 0

    let isCancelled = false
    const showActiveVideo = () => {
      if (isCancelled) return
      setVisibleDirection(playbackDirection)
      setIsVideoVisible(true)
      setAutoplayBlocked(false)
    }

    void Promise.resolve(activeVideo.play()).then(showActiveVideo).catch(() => {
      if (isCancelled) return
      setIsVideoVisible(false)
      setAutoplayBlocked(true)
    })

    return () => {
      isCancelled = true
    }
  }, [forwardSrc, isActive, playbackDirection, reverseSrc])

  const handlePlaying = (direction: BoomerangDirection) => {
    if (!isActive || direction !== playbackDirection) return

    setVisibleDirection(direction)
    setIsVideoVisible(true)
    setAutoplayBlocked(false)
  }

  const handlePause = (direction: BoomerangDirection) => {
    const video = direction === "reverse" ? reverseVideoRef.current : videoRef.current
    if (
      !isActive ||
      direction !== playbackDirection ||
      !video ||
      video.ended ||
      document.visibilityState !== "visible"
    ) {
      return
    }

    setIsVideoVisible(false)
    setAutoplayBlocked(true)
  }

  const handleManualPlayback = () => {
    const video = videoRef.current
    if (!video || !isActive) return

    setPlaybackDirection("forward")
    setVisibleDirection("forward")
    setIsManualPlayback(true)
    video.currentTime = 0
    video.muted = true
    void Promise.resolve(video.play()).catch(() => {
      setIsVideoVisible(false)
      setAutoplayBlocked(true)
    })
  }

  const handleEnded = (direction: BoomerangDirection) => {
    if (!isActive) return

    if (direction === "forward" && isManualPlayback) return

    if (direction === "forward" && reverseSrc) {
      setPlaybackDirection("reverse")
      return
    }

    if (direction === "reverse") {
      setPlaybackDirection("forward")
      return
    }

    const video = videoRef.current
    if (!video) return
    video.currentTime = 0
    void Promise.resolve(video.play()).catch(() => undefined)
  }

  return {
    autoplayBlocked,
    forwardVideoRef: videoRef,
    handleEnded,
    handleManualPlayback,
    handlePause,
    handlePlaying,
    hasVideo: Boolean(forwardSrc),
    isActive,
    isManualPlayback,
    isVideoVisible,
    reverseVideoRef,
    videoRef,
    visibleDirection,
  }
}

export function CharacterCard({
  character,
  variant = "default",
  priority = false,
  sticker,
}: CharacterCardProps) {
  const displayCharacter = useLocalizedCharacter(character)
  const isCompact = variant === "compact"
  const previewText = displayCharacter.tagline || displayCharacter.description || character.tagline
  const conversationCount =
    character.conversationCount && character.conversationCount > 0
      ? character.conversationCount
      : character.messageCount
  const creatorLabel = getCharacterCreatorLabel(character)
  const boomerangVideo = useBoomerangVideo(
    character.previewVideoUrl,
    character.previewReverseVideoUrl
  )

  const handleCardHoverStart = () => {
    if (boomerangVideo.forwardVideoRef.current) {
      setHoveredLimitedVideo(boomerangVideo.forwardVideoRef.current)
    }
  }

  const handleCardHoverEnd = () => {
    if (boomerangVideo.forwardVideoRef.current === hoveredVideo) {
      setHoveredLimitedVideo(null)
    }
  }

  return (
    <LocalizedLink href={ROUTES.chatWith(character.id)} prefetch={false}>
      <div
        onClickCapture={(event) => {
          if (!boomerangVideo.autoplayBlocked) return
          event.preventDefault()
          event.stopPropagation()
          boomerangVideo.handleManualPlayback()
        }}
        onMouseEnter={handleCardHoverStart}
        onMouseLeave={handleCardHoverEnd}
        onFocus={handleCardHoverStart}
        onBlur={handleCardHoverEnd}
        className={cn(
          "group relative overflow-hidden rounded-xl border border-border/50 bg-card",
          "cursor-pointer shadow-[0_18px_42px_rgba(0,0,0,0.28)] transition-[border-color,box-shadow] duration-300 ease-out",
          "hover:border-white/25 hover:shadow-[0_26px_70px_rgba(0,0,0,0.46)]",
          isCompact ? "h-[240px]" : "h-[300px] sm:h-[330px] lg:h-[350px]"
        )}
      >
        {/* Character Image — neutral placeholder matches skeleton; failed/missing
            avatars degrade to a branded placeholder instead of a black box. */}
        <div className="absolute inset-0">
          <ImageWithFallback
            src={character.avatar}
            alt={character.name}
            fill
            sizes={isCompact ? "240px" : "(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw"}
            className="object-cover object-center transition-transform duration-500 ease-out will-change-transform group-hover:scale-105"
            fallbackClassName="bg-muted/80"
            fallback={<ImageFallback className="absolute inset-0" />}
            priority={priority}
            errorScope="character-card-avatar"
            errorExtra={{ character_id: character.id }}
          />
          {boomerangVideo.hasVideo && character.previewVideoUrl && (
            <video
              ref={boomerangVideo.forwardVideoRef}
              className={cn(
                "absolute inset-0 h-full w-full object-cover object-center transition-[opacity,transform] duration-500 ease-out will-change-transform group-hover:scale-105",
                boomerangVideo.isActive &&
                  boomerangVideo.isVideoVisible &&
                  boomerangVideo.visibleDirection === "forward"
                  ? "opacity-100"
                  : "opacity-0"
              )}
              src={boomerangVideo.isActive ? character.previewVideoUrl : undefined}
              autoPlay
              loop={boomerangVideo.isManualPlayback}
              muted
              playsInline
              preload={boomerangVideo.isActive ? "auto" : "none"}
              aria-label={character.name}
              onEnded={() => boomerangVideo.handleEnded("forward")}
              onPause={() => boomerangVideo.handlePause("forward")}
              onPlaying={() => boomerangVideo.handlePlaying("forward")}
            />
          )}
          {boomerangVideo.hasVideo && character.previewReverseVideoUrl && (
            <video
              ref={boomerangVideo.reverseVideoRef}
              className={cn(
                "absolute inset-0 h-full w-full object-cover object-center transition-[opacity,transform] duration-500 ease-out will-change-transform group-hover:scale-105",
                boomerangVideo.isActive &&
                  boomerangVideo.isVideoVisible &&
                  boomerangVideo.visibleDirection === "reverse"
                  ? "opacity-100"
                  : "opacity-0"
              )}
              src={
                boomerangVideo.isActive
                  ? character.previewReverseVideoUrl
                  : undefined
              }
              muted
              playsInline
              preload={boomerangVideo.isActive ? "auto" : "none"}
              aria-label={`${character.name} reverse preview`}
              onEnded={() => boomerangVideo.handleEnded("reverse")}
              onPause={() => boomerangVideo.handlePause("reverse")}
              onPlaying={() => boomerangVideo.handlePlaying("reverse")}
            />
          )}
          {boomerangVideo.isActive && boomerangVideo.autoplayBlocked && (
            <VideoAutoplayFallback />
          )}
        </div>

        {sticker && (
          <div className="absolute right-3 top-3 z-20">
            <div className="relative rounded-[9px] border border-sky-200/55 bg-[linear-gradient(180deg,rgba(236,249,255,0.92)_0%,rgba(113,204,255,0.82)_48%,rgba(39,137,238,0.78)_100%)] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.13em] text-[#06233d] shadow-[0_5px_16px_rgba(35,145,240,0.24),inset_0_1px_0_rgba(255,255,255,0.68)] backdrop-blur-md">
              {sticker}
            </div>
          </div>
        )}

        {/* Bottom overlay with character info */}
        <div className="absolute inset-x-0 bottom-0 z-10 bg-[linear-gradient(180deg,transparent_0%,rgba(0,0,0,0.12)_22%,rgba(0,0,0,0.58)_62%,rgba(0,0,0,0.92)_100%)] p-4 pt-24 [text-shadow:0_1px_10px_rgba(0,0,0,0.65)]">
          <div className="flex min-w-0 items-baseline gap-2">
            <h3
              className={cn(
                "min-w-0 truncate font-semibold text-white",
                isCompact ? "text-sm" : "text-lg"
              )}
            >
              {character.name}
            </h3>
            <span className={cn("shrink-0 font-bold text-white/95", isCompact ? "text-sm" : "text-xl")}>
              {character.age}
            </span>
          </div>
          <p
            className={cn(
              "overflow-hidden text-white/62",
              isCompact ? "text-xs mt-0.5" : "text-sm mt-1"
            )}
            style={{
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
          >
            {previewText}
          </p>

          <div className={cn("mt-2.5 flex min-w-0 items-center gap-2 text-white/85", isCompact ? "text-[10px]" : "text-xs")}>
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex shrink-0 items-center gap-1">
                <MessageCircle className={cn(isCompact ? "h-3 w-3" : "h-3.5 w-3.5")} />
                <span className="font-semibold">{formatConversationCount(conversationCount)}</span>
              </div>
            </div>
            {creatorLabel && (
              <div className="ml-auto flex min-w-0 items-center gap-1 font-semibold text-white/90">
                <UserRound className={cn("shrink-0", isCompact ? "h-3 w-3" : "h-3.5 w-3.5")} />
                <span className="truncate">{creatorLabel}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </LocalizedLink>
  )
}
